// Laden von Board und Liste der Opportunities. Beide hören auf Echtzeit (Opportunity geändert, Lead mit geänderten
// Opportunities) und laden dann still nach, ohne dass die Anzeige springt.
import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { useEchtzeit } from "../api/echtzeit";
import {
  opportunitiesApi,
  type Board,
  type OpportunityFilter,
  type OpportunityListenParameter,
  type OpportunityListe,
  type OpportunitySortierung,
  type OpportunitySumme,
  type OpportunityZeile,
} from "../api/opportunities";

export interface BoardParameter extends OpportunityFilter {
  status_type: "active" | "all";
  sort: OpportunitySortierung;
}

/** Nach einer Änderung kurz warten, damit mehrere Ereignisse hintereinander nur einmal laden. */
function useVerzoegert(f: () => void, ms = 400): () => void {
  const ref = useRef(f);
  ref.current = f;
  const zeit = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(zeit.current), []);
  return useCallback(() => {
    window.clearTimeout(zeit.current);
    zeit.current = window.setTimeout(() => ref.current(), ms);
  }, [ms]);
}

function istOpportunityEreignis(e: { type: string; data?: unknown }): boolean {
  if (e.type === "opportunity.updated") return true;
  const felder = (e.data as { fields?: unknown } | undefined)?.fields;
  return e.type === "lead.updated" && Array.isArray(felder) && felder.includes("opportunities");
}

/** `jeSpalte`: wie viele Karten je Spalte das Board holt (die Seite hebt es an, wenn man Spalten weiter aufgeklappt hat). */
export function useBoard(p: BoardParameter, jeSpalte?: () => number) {
  const [board, setBoard] = useState<Board | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<unknown>(null);
  const generation = useRef(0);
  const schluessel = JSON.stringify(p);

  const laden = useCallback(
    async (still = false) => {
      const gen = ++generation.current;
      if (!still) setLaedt(true);
      try {
        const b = await opportunitiesApi.board({ ...p, je_spalte: jeSpalte?.() });
        if (gen !== generation.current) return;
        setBoard(b);
        setFehler(null);
      } catch (e) {
        if (gen === generation.current) setFehler(e);
      } finally {
        if (gen === generation.current) setLaedt(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schluessel],
  );

  useEffect(() => {
    void laden();
  }, [laden]);

  const planen = useVerzoegert(() => void laden(true));
  useEchtzeit(["opportunity.updated", "lead.updated"], (e) => {
    if (istOpportunityEreignis(e)) planen();
  });

  return { board, setBoard: setBoard as Dispatch<SetStateAction<Board | null>>, laedt, fehler, neuLaden: () => laden(), still: () => laden(true) };
}

/** Karten einer Spalte nachladen (mit dem Cursor des Boards bzw. der letzten Seite), ohne Doppelte. */
export async function spalteNachladen(
  p: BoardParameter,
  statusId: string,
  cursor: string,
  signal?: AbortSignal,
): Promise<{ karten: OpportunityZeile[]; has_more: boolean; cursor: string | null }> {
  const r = await opportunitiesApi.liste(
    { pipeline_id: p.pipeline_id, user_id: p.user_id, smart_view_id: p.smart_view_id, feld: p.feld, von: p.von, bis: p.bis, status_id: [statusId], sort: p.sort, cursor, limit: 50 },
    signal,
  );
  return { karten: r.data, has_more: r.has_more, cursor: r.has_more ? r.cursor : null };
}

export interface ListenFilter extends OpportunityFilter {
  status_id?: string[];
  q?: string;
  sort: OpportunitySortierung;
}

export function useListe(f: ListenFilter) {
  const [zeilen, setZeilen] = useState<OpportunityZeile[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [summe, setSumme] = useState<OpportunitySumme | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [laedtMehr, setLaedtMehr] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);
  const generation = useRef(0);
  const schluessel = JSON.stringify(f);
  const parameter = useCallback((extra: Partial<OpportunityListenParameter> = {}): OpportunityListenParameter => ({ ...f, limit: 100, ...extra }), [f]);

  const anwenden = (r: OpportunityListe) => {
    setZeilen(r.data);
    setTotal(r.total);
    setSumme(r.summe);
    setCursor(r.has_more ? r.cursor : null);
  };

  const laden = useCallback(
    async (still = false) => {
      const gen = ++generation.current;
      if (!still) setLaedt(true);
      try {
        const r = await opportunitiesApi.liste(parameter());
        if (gen !== generation.current) return;
        anwenden(r);
        setFehler(null);
      } catch (e) {
        if (gen === generation.current) setFehler(e);
      } finally {
        if (gen === generation.current) setLaedt(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schluessel],
  );

  useEffect(() => {
    void laden();
  }, [laden]);

  const mehr = useCallback(async () => {
    if (!cursor || laedtMehr) return;
    setLaedtMehr(true);
    const gen = generation.current;
    try {
      const r = await opportunitiesApi.liste(parameter({ cursor }));
      if (gen !== generation.current) return;
      setZeilen((z) => {
        const bekannt = new Set(z.map((x) => x.id));
        return [...z, ...r.data.filter((x) => !bekannt.has(x.id))];
      });
      setCursor(r.has_more ? r.cursor : null);
    } catch (e) {
      setFehler(e);
    } finally {
      setLaedtMehr(false);
    }
  }, [cursor, laedtMehr, parameter]);

  const planen = useVerzoegert(() => void laden(true));
  useEchtzeit(["opportunity.updated", "lead.updated"], (e) => {
    if (istOpportunityEreignis(e)) planen();
  });

  /** Eine Zeile nach einer Änderung ersetzen (ohne Neuladen der ganzen Liste). */
  const ersetzen = useCallback((z: OpportunityZeile) => setZeilen((alt) => alt.map((x) => (x.id === z.id ? z : x))), []);

  return { zeilen, total, summe, cursor, laedt, laedtMehr, fehler, mehr, neuLaden: () => laden(), still: () => laden(true), ersetzen };
}
